import React, { useEffect, useState } from "react";
import { Card, Tag, Toast } from "antd-mobile";
import {
  LeftOutline,
  UnorderedListOutline,
} from "antd-mobile-icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  getScenarioDetail,
  ScenarioDetail as ScenarioDetailType,
  ScenarioStatusEnum,
  CompletionRuleEnum,
} from "../../lib/api/scenario";
import ManyDotLoading from "../../components/Loaders/ManyDotLoading";
import { motion } from "framer-motion";
import ChatStart2 from "../../components/buttons/ChatStart2";

const getStatusBadgeClass = (status: ScenarioStatusEnum) => {
  switch (status) {
    case ScenarioStatusEnum.PUBLISHED:
      return "bg-green-500";
    case ScenarioStatusEnum.DRAFT:
      return "bg-amber-500";
    case ScenarioStatusEnum.ARCHIVED:
      return "bg-gray-300";
    default:
      return "bg-blue-500";
  }
};

const getStatusText = (status: ScenarioStatusEnum) => {
  switch (status) {
    case ScenarioStatusEnum.PUBLISHED:
      return "已发布";
    case ScenarioStatusEnum.DRAFT:
      return "草稿";
    case ScenarioStatusEnum.ARCHIVED:
      return "已归档";
    default:
      return String(status);
  }
};



const formatTime = (timeString?: string) => {
  if (!timeString) return "-";
  return new Date(timeString).toLocaleString("zh-CN");
};

const ScenarioDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [scenarioData, setScenarioData] = useState<ScenarioDetailType | null>(
    null
  );

  const fetchScenarioDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getScenarioDetail(id);
      if (response.data) {
        setScenarioData(response.data);
      }
    } catch (error) {
      Toast.show({ content: "获取场景详情失败", icon: "fail" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenarioDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => navigate(-1);

  const handleStartPractice = () => {
    if (!scenarioData) return;
    navigate(`/scenario-chat/${scenarioData.id}`, {
      state: { scenarioData },
    });
  };

  const handleViewHistory = () => {
    if (!scenarioData) return;
    navigate(`/learn/scenario/${scenarioData.id}/sessions`);
  };

  if (loading) {
    return (
      <motion.div className='min-h-screen bg-gray-100 pb-20 flex justify-center items-center flex-col'>
        <ManyDotLoading />
        <div>加载中...</div>
      </motion.div>
    );
  }

  if (!scenarioData) {
    return (
      <div className='min-h-screen pb-20 text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-[length:200%_200%] animate-gradient-x'>
        <div className='p-5 text-center'>场景不存在</div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-100 pb-20'>
      <motion.div
        initial={{ y: -200 }}
        animate={{ y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          duration: 1,
        }}
      >
        <div className="relative p-5 text-gray-50 bg-yellow-100 border rounded-lg overflow-hidden group hover:text-rose-300 duration-500 before:duration-500 after:duration-500 group-hover:before:duration-500 group-hover:after:duration-500 hover:border-rose-300 hover:before:[box-shadow:_20px_20px_20px_30px_#a21caf] hover:before:right-12 hover:before:-bottom-8 hover:after:-right-8 before:absolute before:w-12 before:h-12 before:content-[''] before:right-1 before:top-1 before:z-0 before:bg-violet-500 before:rounded-full before:blur-lg after:absolute after:z-0 after:w-20 after:h-20 after:content-[''] after:bg-rose-300 after:right-8 after:top-3 after:rounded-full after:blur-lg">
          <div
            className='absolute top-5 left-5 z-10 cursor-pointer flex items-center justify-center w-8 h-8 rounded-full bg-white/20'
            onClick={handleBack}
          >
            <LeftOutline fontSize={18} />
          </div>

          <div className='relative z-10 pt-10'>
            <h1 className='text-2xl font-bold text-black/80 mb-2'>
              {scenarioData.name}
            </h1>
            {scenarioData.description && (
              <p className='text-sm text-white/90 mb-4 leading-relaxed'>
                {scenarioData.description}
              </p>
            )}

            {scenarioData.tags && scenarioData.tags.length > 0 && (
              <div className='flex flex-wrap gap-2 mb-4'>
                {scenarioData.tags.map((tag, index) => (
                  <Tag
                    key={index}
                    className='bg-white/20 text-white border border-white/30 rounded-xl'
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}

            <span
              className={`inline-block px-3 py-1 rounded-xl text-xs font-medium ${getStatusBadgeClass(
                scenarioData.status
              )} text-white`}
            >
              {getStatusText(scenarioData.status)}
            </span>
          </div>
        </div>
      </motion.div>
      <div className='p-4'>
        <Card className='mb-4 rounded-xl shadow-md'>
          <h3 className='text-base font-semibold mb-3 text-gray-800'>
            基础信息
          </h3>
          {scenarioData.key_points && (
            <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
              <span className='text-gray-500 text-sm'>关键要点</span>
              <span className='text-gray-800 text-sm font-medium'>
                {scenarioData.key_points}
              </span>
            </div>
          )}
          {scenarioData.created_by && (
            <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
              <span className='text-gray-500 text-sm'>创建者</span>
              <span className='text-gray-800 text-sm font-medium'>
                {scenarioData.created_by.name}
              </span>
            </div>
          )}
          <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
            <span className='text-gray-500 text-sm'>创建时间</span>
            <span className='text-gray-800 text-sm font-medium'>
              {formatTime(scenarioData.created_at)}
            </span>
          </div>
          <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
            <span className='text-gray-500 text-sm'>更新时间</span>
            <span className='text-gray-800 text-sm font-medium'>
              {formatTime(scenarioData.updated_at)}
            </span>
          </div>
        </Card>

        {scenarioData.ai_roles && scenarioData.ai_roles.length > 0 && (
          <Card className='mb-4 rounded-xl shadow-md'>
            <h3 className='text-base font-semibold mb-3 text-gray-800'>
              AI角色设定
            </h3>

            {scenarioData.ai_roles_overall_description && (
              <div className='mb-3 p-3 bg-blue-50 rounded-lg'>
                <div className='font-semibold mb-1 text-blue-500'>整体设定</div>
                <div className='text-xs text-gray-600 leading-snug'>
                  {scenarioData.ai_roles_overall_description}
                </div>
              </div>
            )}

            {scenarioData.ai_roles.map((role, index) => (
              <div
                key={role.scenario_role_id || index}
                className='bg-gray-50 rounded-lg p-3 mb-3'
              >
                <div className='font-semibold text-gray-800 mb-1'>
                  {role.role_data.name}
                </div>
                <div className='text-xs text-gray-600 leading-snug'>
                  {role.role_data.description}
                </div>
                {role.role_data.communication_style && (
                  <div className='mt-2'>
                    <div className='text-xs font-semibold text-blue-500'>
                      沟通风格
                    </div>
                    <div className='text-xs text-gray-600'>
                      {role.role_data.communication_style}
                    </div>
                  </div>
                )}
                {role.role_data.background && (
                  <div className='mt-2'>
                    <div className='text-xs font-semibold text-blue-500'>
                      背景设定
                    </div>
                    <div className='text-xs text-gray-600'>
                      {role.role_data.background}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}


        {(scenarioData.scoring_criteria_config ||
          (scenarioData.scoring_knowledge_base_ids &&
            scenarioData.scoring_knowledge_base_ids.length > 0)) && (
          <Card className='mb-4 rounded-xl shadow-md'>
            <h3 className='text-base font-semibold mb-3 text-gray-800'>
              评分标准
            </h3>
            {scenarioData.scoring_criteria_config && (
              <div className='bg-gray-50 rounded-lg p-3 mb-2'>
                <div className='font-semibold text-gray-800 mb-1'>评分配置</div>
                <div className='text-xs text-gray-600 leading-snug'>
                  已设置评分标准配置
                </div>
              </div>
            )}
            {scenarioData.scoring_knowledge_base_ids &&
              scenarioData.scoring_knowledge_base_ids.length > 0 && (
                <div className='bg-gray-50 rounded-lg p-3 mb-2'>
                  <div className='font-semibold text-gray-800 mb-1'>知识库</div>
                  <div className='text-xs text-gray-600 leading-snug'>
                    关联了 {scenarioData.scoring_knowledge_base_ids.length}{" "}
                    个知识库
                  </div>
                </div>
              )}
          </Card>
        )}

        {(scenarioData.practice_time_start ||
          scenarioData.practice_time_end ||
          scenarioData.min_practice_duration ||
          scenarioData.max_practice_duration ||
          scenarioData.max_practice_attempts ||
          scenarioData.completion_rule) && (
          <Card className='mb-4 rounded-xl shadow-md'>
            <h3 className='text-base font-semibold mb-3 text-gray-800'>
              陪练设置
            </h3>
            {scenarioData.practice_time_start && (
              <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                <span className='text-gray-500 text-sm'>开始时间</span>
                <span className='text-gray-800 text-sm font-medium'>
                  {new Date(scenarioData.practice_time_start).toLocaleString()}
                </span>
              </div>
            )}
            {scenarioData.practice_time_end && (
              <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                <span className='text-gray-500 text-sm'>结束时间</span>
                <span className='text-gray-800 text-sm font-medium'>
                  {new Date(scenarioData.practice_time_end).toLocaleString()}
                </span>
              </div>
            )}
            {scenarioData.min_practice_duration && (
              <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                <span className='text-gray-500 text-sm'>最小练习时长</span>
                <span className='text-gray-800 text-sm font-medium'>
                  {scenarioData.min_practice_duration} 分钟
                </span>
              </div>
            )}
            {scenarioData.max_practice_duration && (
              <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                <span className='text-gray-500 text-sm'>最大练习时长</span>
                <span className='text-gray-800 text-sm font-medium'>
                  {scenarioData.max_practice_duration} 分钟
                </span>
              </div>
            )}
            {scenarioData.max_practice_attempts && (
              <div className='flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0'>
                <span className='text-gray-500 text-sm'>最大练习次数</span>
                <span className='text-gray-800 text-sm font-medium'>
                  {scenarioData.max_practice_attempts} 次
                </span>
              </div>
            )}
          </Card>
        )}
      </div>

      <motion.div
        initial={{ y: 200 }}
        animate={{ y: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          duration: 1,
        }}
        className='fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-100 z-[100]'
      >
        <div className='flex gap-3 items-center justify-between'>
          <div className="" onClick={handleViewHistory}><UnorderedListOutline  className='mr-1' /></div>
          <div onClick={handleStartPractice}>
            <ChatStart2 />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ScenarioDetail;
